import React, { useState } from 'react';
import { X, KeyRound, AlertTriangle, Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService from '../../services/caregiverService';

/**
 * Reset PIN modal (FECOA-1373). Issues a system-generated 6-digit temp PIN,
 * displays it once on screen, never emails. SC must communicate out-of-band.
 *
 * Props:
 *   caregiverId
 *   caregiverLabel  optional name + caregiver_id displayed in the body
 *   onClose
 *   onResetComplete  called after the SC clicks "Done — PIN Communicated"
 */
const ResetPinModal = ({ caregiverId, caregiverLabel, onClose, onResetComplete }) => {
  const [loading, setLoading] = useState(true);
  const [tempPin, setTempPin] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const data = await caregiverService.resetPin(caregiverId);
        if (!cancelled) setTempPin(data.temporary_pin);
      } catch (err) {
        console.error('reset pin failed', err);
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Failed to reset PIN.');
          toast.error('Failed to reset PIN.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [caregiverId]);

  const masked = tempPin ? '• '.repeat(tempPin.length).trim() : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-primary-500 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Temporary PIN Generated</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-start gap-3 p-3 mb-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 rounded-md">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 dark:text-amber-200">
            This PIN will not be emailed. Read it to the caregiver by phone or write it on their
            appointment card.
          </p>
        </div>

        {caregiverLabel && (
          <div className="mb-3">
            <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Caregiver</p>
            <p className="text-sm text-gray-900 dark:text-white">{caregiverLabel}</p>
          </div>
        )}

        <div className="mb-2">
          <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Temporary PIN</p>
          {loading ? (
            <div className="py-3">
              <Loader2 className="w-5 h-5 animate-spin text-gray-500 dark:text-gray-400" />
            </div>
          ) : error ? (
            <p className="text-sm text-rose-600 dark:text-rose-400 py-2">{error}</p>
          ) : (
            <div className="flex items-center gap-2 mt-1 px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-md">
              <span className="text-xl tracking-widest font-mono text-primary-700 dark:text-primary-300 select-all">
                {reveal ? tempPin : masked}
              </span>
              <button
                type="button"
                onClick={() => setReveal((r) => !r)}
                className="ml-auto text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                aria-label={reveal ? 'Hide PIN' : 'Show PIN'}
              >
                {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            The caregiver will be required to set a new PIN on first login.
          </p>
        </div>

        {!loading && !error && (
          <div className="flex items-start gap-2 p-3 mb-4 bg-primary-50 dark:bg-primary-500/10 border border-primary-300 dark:border-primary-500/30 rounded-md">
            <CheckCircle2 className="w-4 h-4 text-primary-600 dark:text-primary-400 mt-0.5" />
            <div>
              <p className="text-xs text-primary-700 dark:text-primary-300 font-medium">Audit trail updated</p>
              <p className="text-xs text-primary-700/80 dark:text-primary-400/80">
                PIN reset logged with timestamp and your coordinator ID. The previous PIN has been invalidated.
              </p>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => onResetComplete && onResetComplete()}
          disabled={loading || !!error}
          className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-md text-sm"
        >
          Done &mdash; PIN Communicated
        </button>
      </div>
    </div>
  );
};

export default ResetPinModal;
